// // 1. Open (or create) a database
// export function openDatabase(databaseName, store, keyPath) {
// 	return new Promise((resolve, reject) => {
// 		const request = indexedDB.open(databaseName, 1);

// 		request.onupgradeneeded = (event) => {
// 			const db = event.target.result;
// 			if (!db.objectStoreNames.contains(store)) {
// 				db.createObjectStore(store, { keyPath: keyPath });
// 			}
// 		};

// 		request.onsuccess = (event) => {
// 			resolve(event.target.result);
// 		};

// 		request.onerror = (event) => {
// 			reject(event.target.error);
// 		};
// 	});
// }

// // 2. Store data with a key
// export async function storeData(databaseName, storeName, keyPath, key, value) {
// 	const db = await openDatabase(databaseName, storeName, keyPath);

// 	return new Promise((resolve, reject) => {
// 		const transaction = db.transaction([storeName], "readwrite");
// 		const objectStore = transaction.objectStore(storeName);

// 		const request = objectStore.put({ [keyPath]: key, data: value });

// 		request.onsuccess = () => resolve();
// 		request.onerror = () => reject(request.error);
// 	});
// }

// export async function deleteData(databaseName, storeName, keyPath, key) {
// 	const db = await openDatabase(databaseName, storeName, keyPath);

// 	return new Promise((resolve, reject) => {
// 		const transaction = db.transaction([storeName], "readwrite");
// 		const objectStore = transaction.objectStore(storeName);

// 		const request = objectStore.delete(key);

// 		request.onsuccess = () => resolve();
// 		request.onerror = () => reject(request.error);
// 	});
// }

// export async function getData(databaseName, storeName, keyPath, key) {
// 	const db = await openDatabase(databaseName, storeName, keyPath);
// 	console.log("Database", db);
// 	return new Promise((resolve, reject) => {
// 		const transaction = db.transaction(storeName, "readonly");
// 		const store = transaction.objectStore(storeName);
// 		const request = store.get(key);

// 		request.onsuccess = () => {
// 			resolve(request.result ?? null);
// 		};

// 		request.onerror = () => {
// 			reject(request.error);
// 		};
// 	});
// }

// indexedDBHelper.js

/**
 * Internal helper to open the DB and handle dynamic store creation
 */
async function getDB(dbName, storeName, keyPath = "id") {
	// 1. Get current version or default to 1
	let db = await new Promise((resolve, reject) => {
		const req = indexedDB.open(dbName);
		req.onsuccess = () => resolve(req.result);
		req.onerror = () => reject(req.error);
	});

	// 2. If store exists, we are good to go
	if (db.objectStoreNames.contains(storeName)) {
		return db;
	}

	// 3. If store is missing, upgrade the version
	const newVersion = db.version + 1;
	db.close(); // MUST close current connection to allow upgrade

	return new Promise((resolve, reject) => {
		const request = indexedDB.open(dbName, newVersion);

		request.onupgradeneeded = (event) => {
			const upgradeDb = event.target.result;
			if (!upgradeDb.objectStoreNames.contains(storeName)) {
				upgradeDb.createObjectStore(storeName, { keyPath: keyPath });
			}
		};

		request.onsuccess = (event) => resolve(event.target.result);
		request.onerror = (event) => reject(event.target.error);
		request.onblocked = () =>
			console.warn("Database upgrade blocked. Close other tabs.");
	});
}

// --- EXPORTED FUNCTIONS ---

export async function storeData(dbName, storeName, id, data, keyPath = "id") {
	// We pass the keyPath to getDB to ensure the store is created correctly if it's new
	const db = await getDB(dbName, storeName, keyPath);

	return new Promise((resolve, reject) => {
		const tx = db.transaction(storeName, "readwrite");
		const store = tx.objectStore(storeName);

		// Prepare the object: If data is already an object, spread it;
		// otherwise, wrap it. This ensures the 'id' is always present.
		const record =
			typeof data === "object" && data !== null
				? { ...data, [keyPath]: id }
				: { [keyPath]: id, value: data };

		const request = store.put(record);

		request.onsuccess = () => resolve();
		request.onerror = () => reject(request.error);
		tx.oncomplete = () => db.close();
		return record;
	});
}

export async function getData(dbName, storeName, key) {
	const db = await getDB(dbName, storeName);
	return new Promise((resolve, reject) => {
		const tx = db.transaction(storeName, "readonly");
		const store = tx.objectStore(storeName);
		const request = store.get(key);

		request.onsuccess = () => resolve(request.result || null);
		request.onerror = () => reject(request.error);
		tx.oncomplete = () => db.close();
	});
}

export async function getAllData(dbName, storeName) {
	const db = await getDB(dbName, storeName);

	return new Promise((resolve, reject) => {
		const tx = db.transaction(storeName, "readonly");
		const store = tx.objectStore(storeName);
		const request = store.getAll(); // Fetches all records

		request.onsuccess = () => resolve(request.result || []);
		request.onerror = () => reject(request.error);
		tx.oncomplete = () => db.close();
	});
}

export async function deleteData(dbName, storeName, key) {
	const db = await getDB(dbName, storeName);
	return new Promise((resolve, reject) => {
		const tx = db.transaction(storeName, "readwrite");
		const store = tx.objectStore(storeName);
		const request = store.delete(key);

		request.onsuccess = () => resolve();
		request.onerror = () => reject(request.error);
		tx.oncomplete = () => db.close();
	});
}

/**
 * EDIT: An alias for storeData to make code more readable.
 * It will overwrite the existing record with the same ID.
 */
export async function editData(
	dbName,
	storeName,
	id,
	updatedData,
	keyPath = "id",
) {
	// This uses the exact same logic as storeData
	return await storeData(dbName, storeName, id, updatedData, keyPath);
}

/**
 * DELETE: Removes a specific record by its ID.
 */
export async function deleteDatabyId(dbName, storeName, id) {
	const db = await getDB(dbName, storeName);

	return new Promise((resolve, reject) => {
		const tx = db.transaction(storeName, "readwrite");
		const store = tx.objectStore(storeName);

		// request to delete the record with the specific ID
		const request = store.delete(id);

		request.onsuccess = () => resolve();
		request.onerror = () => reject(request.error);
		tx.oncomplete = () => db.close();
	});
}
